<template>
  <Flex column v-if="isContextMenuVisible" v-bind:style="contextMenuStyle" class="context-menu">
    <Flex v-if="isMovingMarkerVisible" class="menu-item" @click="moveMarker">
      <Flex label v-html="$t('poi.menu.move')" />
    </Flex>
    <Flex v-if="isAddMenuVisible" class="menu-item" @click="addPoi">
      <Flex label v-html="$t('poi.menu.add')" />
    </Flex>
    <Flex v-if="isPolygonAddMenuVisible" class="menu-item" @click="addPolygon">
      <Flex label v-html="$t('polygon.menu.add')" />
    </Flex>
    <Flex v-if="isEditMenuVisible" class="menu-item" @click="editPoi">
      <Flex label v-html="$t('poi.menu.edit')" />
    </Flex>
    <Flex v-if="isDeleteMenuVisible" class="menu-item" @click="deletePoi">
      <Flex label v-html="$t('poi.menu.delete')" />
    </Flex>
    <Flex v-if="isPolygonEditMenuVisible" class="menu-item" @click="editPolygon">
      <Flex label v-html="$t('polygon.menu.edit')" />
    </Flex>
    <Flex v-if="isPolygonDeleteMenuVisible" class="menu-item" @click="deletePolygon">
      <Flex label v-html="$t('polygon.menu.delete')" />
    </Flex>
    <div v-if="isComponentAddMenuVisible">
      <Flex
        v-for="type in getCreatableTypes"
        :key="type.menu"
        class="menu-item"
        @click="createRecord(type)"
      >
        <Flex label v-html="type.menu" />
      </Flex>
    </div>
  </Flex>
</template>

<script>
/* eslint-disable */
import Flex from '@wizer/core/components/flex/Flex'
import _, { sortBy } from 'lodash'

export default {
  /**
   * Components
   */
  components: {
    Flex,
  },

  /**
   * This components properties
   */
  props: {
    enablePointsOfInterest: Boolean,
    enablePolygon: Boolean,
    markerReadOnly: Boolean,
    markerPresent: Boolean,
    poiSecurity: Object,
    polygonSecurity: Object,
    recordCreatableTypes: String,
    mapProperties: Object,
  },

  /**
   * This components data attributes
   */
  data() {
    return {
      clickData: Object,
      clickMarker: Object,
      clickLatLng: Object,

      searching: false,

      movingMarker: false,
      addingPOI: false,
      editingPOI: false,
      deletingPOI: false,

      addingPolygon: false,
      editingPolygon: false,
      deletingPolygon: false,

      addingComponent: false,

      contextMenuX: 0,
      contextMenuY: 0,
    }
  },

  /**
   * Computed properties
   */
  computed: {
    isContextMenuVisible() {
      return (
        this.movingMarker ||
        this.addingPOI ||
        this.editingPOI ||
        this.deletingPOI ||
        this.addingPolygon ||
        this.editingPolygon ||
        this.addingComponent
      )
    },
    contextMenuStyle() {
      return {
        left: `${this.contextMenuX}px`,
        top: `${this.contextMenuY}px`,
      }
    },
    isMovingMarkerVisible() {
      return !this.markerReadOnly && this.movingMarker
    },
    isAddMenuVisible() {
      return this.enablePointsOfInterest && this.poiSecurity.creatable && this.addingPOI
    },
    isEditMenuVisible() {
      return this.enablePointsOfInterest && this.editingPOI
    },
    isDeleteMenuVisible() {
      return this.enablePointsOfInterest && this.poiSecurity.deletable && this.deletingPOI
    },
    isPolygonAddMenuVisible() {
      return this.enablePolygon && this.polygonSecurity.creatable && this.addingPolygon
    },
    isPolygonEditMenuVisible() {
      return this.enablePolygon && this.polygonSecurity.writable && this.editingPolygon
    },
    isPolygonDeleteMenuVisible() {
      return this.enablePolygon && this.polygonSecurity.deletable && this.deletingPolygon
    },
    isComponentAddMenuVisible() {
      return this.addingComponent
    },
    getCreatableTypes() {
      let types = JSON.parse(this.recordCreatableTypes)
      return sortBy(types, 'menu')
    },
    enableComponentMenu() {
      return this.getCreatableTypes.length > 0
    },
  },

  /**
   * Component methods
   */
  methods: {
    initializeContext(map) {
      if (!this.markerReadOnly) {
        // pin location on map
        map.addListener('click', (event) => {
          this.evaluateContextMenu(event)
        })
        if (this.enablePointsOfInterest || this.enablePolygon || this.enableComponentMenu) {
          // POI context menu, Polygon context menu and component context menu
          map.addListener('rightclick', this.onRightClick)
        }
        if (this.enablePointsOfInterest) {
          // POI context menu on poi features
          map.data.addListener('rightclick', (event) => {
            if (
              event.feature.getProperty('isEditable') === true ||
              event.feature.getProperty('isDeletable') === true
            ) {
              this.onRightClick(event, event.feature.getProperty('id'))
            }
          })
        }
      }

      map.addListener('bounds_changed', () => {
        if (!this.searching) {
          this.hideContextMenu()
        }
        this.searching = false
      })
    },

    evaluateContextMenu(event, latLng) {
      const menuWasVisible = this.isContextMenuVisible
      this.hideContextMenu()
      if (event) {
        // we are coming from a click event
        if (menuWasVisible) {
          // if the menu was visible and the user clicked somewhere else, just close it
          return
        }
        const mouseEvent = this.getMouseEvent(event)
        if (mouseEvent) {
          this.contextMenuX = mouseEvent.clientX
          this.contextMenuY = mouseEvent.clientY
        }
        this.clickLatLng = event.latLng
      } else if (latLng) {
        // we are coming from a search result selected
        this.searching = true
        this.contextMenuX = window.innerWidth / 2
        this.contextMenuY = window.innerHeight / 2
        this.clickLatLng = latLng
      }
      this.movingMarker = true
    },

    getMouseEvent(event) {
      return Object.values(event).find((value) =>
        Object.prototype.isPrototypeOf.call(MouseEvent.prototype, value),
      )
    },

    onRightClick(event, id, marker) {
      // setTimeout is a workaround so that browser context menu doesn't appear
      // https://stackoverflow.com/questions/49089656/google-maps-v3-prevent-context-menu-on-right-click-on-marker
      setTimeout(() => {
        const mouseEvent = this.getMouseEvent(event)
        this.contextMenuX = mouseEvent.clientX
        this.contextMenuY = mouseEvent.clientY
        this.clickMarker = marker
        this.hideContextMenu()
        if (id) {
          this.clickLatLng = event.latLng
          this.clickData = { id }
          const isPolygon = marker.latLngs != null
          this.editingPOI = !isPolygon
          this.editingPolygon = this.polygonSecurity.writable && isPolygon
          this.deletingPOI = this.poiSecurity.deletable && !isPolygon
          this.addingPOI = false
          this.addingPolygon = false
          this.deletingPolygon = this.polygonSecurity.deletable && isPolygon
          this.addingComponent = true
        } else {
          this.clickLatLng = event.latLng
          this.addingPOI = this.poiSecurity.creatable
          this.addingPolygon = this.polygonSecurity.creatable
          this.editingPOI = false
          this.deletingPOI = false
          this.editingPolygon = false
          this.deletingPolygon = false
          this.addingComponent = true
        }
      }, 0)
    },

    moveMarker() {
      this.hideContextMenu()
      this.$emit('mapMarkerEvent', this.clickLatLng)
    },

    addPoi() {
      this.hideContextMenu()
      this.$emit('poiMarkerAddEvent', this.clickLatLng)
    },

    addPolygon() {
      this.hideContextMenu()
      this.$emit('polygonAddEvent', this.clickLatLng)
    },

    editPoi() {
      this.hideContextMenu()
      this.$emit('poiMarkerEditEvent', this.clickData.id)
    },

    editPolygon() {
      this.hideContextMenu()
      this.$emit('polygonEditEvent', this.clickLatLng, this.clickData.id)
    },

    deletePolygon() {
      this.hideContextMenu()
      this.$emit('polygonDeleteEvent', this.clickData.id, this.clickMarker)
    },

    deletePoi() {
      this.hideContextMenu()
      this.$emit('poiMarkerDeleteEvent', this.clickData.id, this.clickMarker)
    },

    poiDraggable(event, id, marker) {
      this.$emit('poiMarkerDragableEvent', id, marker)
    },

    hideContextMenu() {
      this.movingMarker = false
      this.addingPOI = false
      this.addingPolygon = false
      this.editingPOI = false
      this.deletingPOI = false
      this.editingPolygon = false
      this.addingComponent = false
    },
    createRecord(type) {
      let values = {
        [type.latitude]: this.clickLatLng.lat(),
        [type.longitude]: this.clickLatLng.lng(),
      }
      if (type.mapField) {
        _.assign(values, {
          [type.mapField]: JSON.stringify(this.mapProperties)
        })
      }
      const url = `${type.url}?values=${JSON.stringify(values)}`
      window.open(url, '_blank').focus()
      this.hideContextMenu()
    },
  },
}
</script>
<style scoped lang="postcss">
.context-menu {
  @apply absolute bg-gray-100 text-xs cursor-pointer;
}
.context-menu {
  border-color: #1d4ed8;
  border-width: 1px;
}
.context-menu:before {
  content: '';
  position: absolute;
  top: -1px;
  left: -1px;
  transform: translate(-50%, -50%);
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #1d4ed8;
  background-repeat: no-repeat;
}
.menu-item {
  @apply py-1 px-2;
}
.menu-item:hover {
  @apply bg-gray-300;
}
</style>
